import React from 'react'
import { useTranslation } from 'react-i18next'
import { Avatar, type PieChartDataPoint } from '@node-space/ui-components'
import { Portfolio } from 'types/rewards'

export interface RewardsSummaryData {
  totalBalance: number
  totalEarnings: number
  totalPendingEarnings: number
  portfoliosSummary: PieChartDataPoint[]
}

const defaultRewardsSummaryData: RewardsSummaryData = {
  totalBalance: 0,
  totalEarnings: 0,
  totalPendingEarnings: 0,
  portfoliosSummary: [],
}

const USDC_CHART_COLOR = 'var(--accent-status-info)'
const USDG_CHART_COLOR = 'var(--accent-status-feature)'

export const useRewardsSummary = (portfolios: Portfolio[]): RewardsSummaryData => {
  const { t } = useTranslation()

  if (!portfolios || portfolios.length === 0) {
    return defaultRewardsSummaryData
  }

  return portfolios.reduce((acc, portfolio) => {
    const balance = Number.parseFloat(portfolio?.balance?.totalInBaseCurrency ?? '0') || 0
    const earnings = Number.parseFloat(portfolio?.totalEarnedAmount ?? '0') || 0
    const available = Number.parseFloat(portfolio?.balance?.available ?? '0') || 0
    const blocked = Number.parseFloat(portfolio?.balance?.blocked ?? '0') || 0

    const color = portfolio.currency === 'USDC' ? USDC_CHART_COLOR : USDG_CHART_COLOR
    const opacity = portfolio.currency === 'USDC' ? 0.5 : 0.7

    return {
      totalBalance: acc.totalBalance + balance,
      totalEarnings: acc.totalEarnings + earnings,
      totalPendingEarnings: acc.totalPendingEarnings + blocked,
      portfoliosSummary: [
        ...acc.portfoliosSummary,
        {
          name: portfolio.currency,
          value: available,
          color,
          Avatar: <Avatar size="small" type="currency" name={portfolio.currency} />,
          tooltipLabel: t('rewards.allocated'),
        },
        ...(blocked > 0
          ? [
              {
                name: portfolio.currency,
                value: blocked,
                color: `rgba(from ${color} r g b / ${opacity})`,
                Avatar: <Avatar size="small" type="currency" name={portfolio.currency} />,
                tooltipLabel: t('rewards.pending'),
              },
            ]
          : []),
      ],
    }
  }, defaultRewardsSummaryData)
}
